import React, { useState, useEffect, useRef } from 'react';
import { 
  Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Button, TextField,
  Box, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, CircularProgress, MenuItem,
  Chip, Divider, List, ListItem, ListItemText,
  DialogContentText, Card, CardContent
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, History as HistoryIcon, AttachMoney as AttachMoneyIcon, DirectionsBike as DirectionsBikeIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '../../utils/api';
import MaintenanceDetails from '../maintenance/MaintenanceDetails';
import TablePagination from '@mui/material/TablePagination';
import InputAdornment from '@mui/material/InputAdornment';
import ClearIcon from '@mui/icons-material/Clear';
import logger from '../../utils/logger';

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openMaintenanceDetails, setOpenMaintenanceDetails] = useState(false);
  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState(null);
  const [openSalesDialog, setOpenSalesDialog] = useState(false);
  const [customerSales, setCustomerSales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [selectedSaleForDetails, setSelectedSaleForDetails] = useState(null);
  const [openSaleDetailsDialog, setOpenSaleDetailsDialog] = useState(false);
  const [openRentalsDialog, setOpenRentalsDialog] = useState(false);
  const [customerRentals, setCustomerRentals] = useState([]);
  const [loadingRentals, setLoadingRentals] = useState(false);
  const [selectedRentalForDetails, setSelectedRentalForDetails] = useState(null);
  const [openRentalDetailsDialog, setOpenRentalDetailsDialog] = useState(false);
  const [rentalVehicles, setRentalVehicles] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    countryCode: '39', // Default to Italy
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    notes: '',
    documentType: '',
    documentNumber: '',
    documentIssueDate: '',
    documentExpiryDate: '',
    documentIssuedBy: '',
    vehicles: [{
      brand: '',
      model: '',
      type: 'Bici Elettrica',
      serialNumber: '',
      color: '',
      notes: ''
    }]
  });
  const [openNewTypeDialog, setOpenNewTypeDialog] = useState(false);
  const [newType, setNewType] = useState('');
  const [vehicleTypes, setVehicleTypes] = useState(['Bici Elettrica', 'Bici Muscolare', 'Monopattino', 'Altro']);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(0);
  const [orderBy, setOrderBy] = useState('lastName');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const submittingRef = useRef(false);

  // Fetch customers on component mount
  useEffect(() => {
    fetchCustomers();
    
    // Check if we should open the new customer dialog
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('new') === 'true') {
      handleOpenDialog();
      // Remove the query parameter from the URL
      window.history.replaceState({}, '', '/customers');
    }
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      logger.debug('Fetching customers from API...');
      const res = await api.get('/customers');
      logger.debug('Customers data received:', res.data.length);
      setCustomers(res.data);
      setError(null);
    } catch (err) {
      logger.error('Error fetching customers:', err);
      // More descriptive error message
      setError(`Errore nel caricamento dei clienti: ${err.message}. Verifica che il server API sia in esecuzione.`);
      // Set customers to empty array to avoid undefined errors
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.trim() === '') {
      fetchCustomers();
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/customers/search/${searchTerm}`);
      setCustomers(res.data);
      setError(null);
    } catch (err) {
      console.error('Error searching customers:', err);
      setError(`Errore nella ricerca dei clienti: ${err.message}`);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setCurrentCustomer(customer);
      // Fetch vehicles for this customer
      api.get(`/vehicles/customer/${customer.id}`)
        .then(res => {
          const customerVehicles = res.data.length > 0 ? res.data : [{
            brand: '',
            model: '',
            type: 'Bici Elettrica',
            serialNumber: '',
            color: '',
            notes: ''
          }];
          
          setFormData({
            firstName: customer.firstName,
            lastName: customer.lastName,
            countryCode: customer.countryCode || '39',
            phone: customer.phone,
            email: customer.email || '',
            address: customer.address || '',
            city: customer.city || '',
            postalCode: customer.postalCode || '',
            notes: customer.notes || '',
            documentType: customer.documentType || '',
            documentNumber: customer.documentNumber || '',
            documentIssueDate: customer.documentIssueDate || '',
            documentExpiryDate: customer.documentExpiryDate || '',
            documentIssuedBy: customer.documentIssuedBy || '',
            vehicles: customerVehicles
          });
        })
        .catch(err => {
          console.error('Error fetching vehicles:', err);
          setFormData({
            firstName: customer.firstName,
            lastName: customer.lastName,
            countryCode: customer.countryCode || '39',
            phone: customer.phone,
            email: customer.email || '',
            address: customer.address || '',
            city: customer.city || '',
            postalCode: customer.postalCode || '',
            notes: customer.notes || '',
            documentType: customer.documentType || '',
            documentNumber: customer.documentNumber || '',
            documentIssueDate: customer.documentIssueDate || '',
            documentExpiryDate: customer.documentExpiryDate || '',
            documentIssuedBy: customer.documentIssuedBy || '',
            vehicles: [{
              brand: '',
              model: '',
              type: 'Bici Elettrica',
              serialNumber: '',
              color: '',
              notes: ''
            }]
          });
        });
    } else {
      setCurrentCustomer(null);
      setFormData({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postalCode: '',
        notes: '',
        documentType: '',
        documentNumber: '',
        documentIssueDate: '',
        documentExpiryDate: '',
        documentIssuedBy: '',
        vehicles: [{
          brand: '',
          model: '',
          type: 'Bici Elettrica',
          serialNumber: '',
          color: '',
          notes: ''
        }]
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Check if this is a vehicle field (contains vehicle index)
    if (name.includes('vehicle-')) {
      const parts = name.split('-');
      const vehicleIndex = parseInt(parts[1]);
      const fieldName = parts[2];
      
      // Gestione speciale per l'aggiunta di una nuova tipologia
      if (fieldName === 'type' && value === 'new_type') {
        setSelectedVehicleIndex(vehicleIndex);
        setOpenNewTypeDialog(true);
        return;
      }
      
      const updatedVehicles = [...formData.vehicles];
      updatedVehicles[vehicleIndex] = {
        ...updatedVehicles[vehicleIndex],
        [fieldName]: fieldName === 'type' ? value : value.toUpperCase()
      };
      
      setFormData({
        ...formData,
        vehicles: updatedVehicles
      });
    } else {
      // Regular field - convert to uppercase
      setFormData({
        ...formData,
        [name]: value.toUpperCase()
      });
    }
  };
  
  // Function to add a new vehicle form
  const addVehicle = () => {
    setFormData({
      ...formData,
      vehicles: [
        ...formData.vehicles,
        {
          brand: '',
          model: '',
          type: 'Bici Elettrica',
          serialNumber: '',
          color: '',
          notes: ''
        }
      ]
    });
  };
  
  // Function to remove a vehicle form
  const removeVehicle = (index) => {
    if (formData.vehicles.length > 1) {
      const updatedVehicles = formData.vehicles.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        vehicles: updatedVehicles
      });
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = ['firstName', 'lastName', 'phone'];
    const emptyFields = requiredFields.filter(field => !formData[field].trim());
    
    if (emptyFields.length > 0) {
      const fieldNames = {
        firstName: 'Nome',
        lastName: 'Cognome',
        phone: 'Telefono'
      };
      
      const missingFields = emptyFields.map(field => fieldNames[field]).join(', ');
      alert(`I seguenti campi sono obbligatori: ${missingFields}`);
      return;
    }
    
    // Validate email format if provided
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      alert('Formato email non valido');
      return;
    }
    
    // Validate vehicle required fields
    const vehicleRequiredFields = ['brand', 'model', 'type'];
    let invalidVehicles = false;
    
    formData.vehicles.forEach((vehicle, index) => {
      const emptyVehicleFields = vehicleRequiredFields.filter(field => !vehicle[field]);
      if (emptyVehicleFields.length > 0) {
        const fieldNames = {
          brand: 'Marca',
          model: 'Modello',
          type: 'Tipologia'
        };
        
        const missingFields = emptyVehicleFields.map(field => fieldNames[field]).join(', ');
        alert(`Veicolo ${index + 1}: I seguenti campi sono obbligatori: ${missingFields}`);
        invalidVehicles = true;
      }
    });
    
    if (invalidVehicles) return;
    
    if (savingCustomer || submittingRef.current) return;
    submittingRef.current = true;
    setSavingCustomer(true);
    try {
      let customerId;
      
      if (currentCustomer) {
        // Update existing customer
        const customerResponse = await api.put(`/customers/${currentCustomer.id}`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          notes: formData.notes
        });
        customerId = currentCustomer.id;
        
        // Fetch existing vehicles to compare
        const existingVehicles = await api.get(`/vehicles/customer/${customerId}`);
        
        // For each vehicle in the form: create only once per (id or brand+model+serialNumber) to avoid duplicates
        const createdKeys = new Set();
        for (const vehicle of formData.vehicles) {
          if (vehicle.id) {
            // Update existing vehicle
            await api.put(`/vehicles/${vehicle.id}`, {
              ...vehicle,
              customerId
            });
          } else {
            const key = `${vehicle.brand}|${vehicle.model}|${vehicle.serialNumber || ''}`;
            if (createdKeys.has(key)) continue;
            createdKeys.add(key);
            await api.post('/vehicles', {
              ...vehicle,
              customerId
            });
          }
        }
        
        // Delete vehicles that were removed
        for (const existingVehicle of existingVehicles.data) {
          const stillExists = formData.vehicles.some(v => v.id === existingVehicle.id);
          if (!stillExists) {
            await api.delete(`/vehicles/${existingVehicle.id}`);
          }
        }
      } else {
        // Create new customer
        const customerResponse = await api.post('/customers', {
          firstName: formData.firstName,
          lastName: formData.lastName,
          countryCode: formData.countryCode,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          notes: formData.notes
        });
        
        customerId = customerResponse.data.id;
        
        // Create vehicles for the new customer
        for (const vehicle of formData.vehicles) {
          if (vehicle.brand && vehicle.model) { // Only create if has required fields
            await api.post('/vehicles', {
              ...vehicle,
              customerId
            });
          }
        }
      }
      
      fetchCustomers();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving customer or vehicles:', err);
      alert(`Errore nel salvare i dati: ${err.message}`);
    } finally {
      setSavingCustomer(false);
      submittingRef.current = false;
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questo cliente?')) {
      try {
        await api.delete(`/customers/${id}`);
        fetchCustomers();
      } catch (err) {
        console.error('Error deleting customer:', err);
        if (err.response && err.response.status === 400) {
          alert(err.response.data.msg);
        } else {
          alert(`Errore nell'eliminare il cliente: ${err.message}`);
        }
      }
    }
  };

  // Function to fetch maintenance history for a customer
  const fetchMaintenanceHistory = async (customerId) => {
    setLoadingHistory(true);
    try {
      const res = await api.get(`/maintenance/customer/${customerId}`);
      setMaintenanceHistory(res.data);
    } catch (err) {
      console.error('Error fetching maintenance history:', err);
      setMaintenanceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Function to open maintenance history dialog
  const handleOpenHistoryDialog = (customer) => {
    setCurrentCustomer(customer);
    fetchMaintenanceHistory(customer.id);
    setOpenHistoryDialog(true);
  };

  // Function to close maintenance history dialog
  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false);
  };

  // Function to handle clicking on a maintenance in the history dialog
  const handleMaintenanceClick = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setOpenMaintenanceDetails(true);
  };

  // Function to close maintenance details dialog
  const handleCloseMaintenanceDetails = () => {
    setOpenMaintenanceDetails(false);
    setSelectedMaintenanceId(null);
  };

  // Function to fetch customer sales
  const fetchCustomerSales = async (customerId) => {
    setLoadingSales(true);
    try {
      const res = await api.get(`/sales`);
      // Filtra le vendite per questo cliente
      const sales = res.data.filter(sale => 
        sale.customerId === customerId || 
        sale.Customer?.id === customerId ||
        sale.customer?.id === customerId
      );
      setCustomerSales(sales);
    } catch (err) {
      console.error('Error fetching customer sales:', err);
      setCustomerSales([]);
    } finally {
      setLoadingSales(false);
    }
  };

  // Function to open sales dialog
  const handleOpenSalesDialog = (customer) => {
    setCurrentCustomer(customer);
    fetchCustomerSales(customer.id);
    setOpenSalesDialog(true);
  };

  // Function to close sales dialog
  const handleCloseSalesDialog = () => {
    setOpenSalesDialog(false);
    setCustomerSales([]);
  };

  // Function to handle clicking on a sale
  const handleSaleClick = async (saleId) => {
    try {
      const res = await api.get(`/sales/${saleId}`);
      setSelectedSaleForDetails(res.data);
      setOpenSaleDetailsDialog(true);
    } catch (err) {
      console.error('Error fetching sale details:', err);
    }
  };

  // Function to close sale details dialog
  const handleCloseSaleDetailsDialog = () => {
    setOpenSaleDetailsDialog(false);
    setSelectedSaleForDetails(null);
  };

  // Function to fetch customer rentals
  const fetchCustomerRentals = async (customerId) => {
    setLoadingRentals(true);
    try {
      const [rentalsRes, vehiclesRes] = await Promise.all([
        api.get(`/rentals`),
        api.get(`/rental-vehicles`)
      ]);
      
      // Filtra i noleggi per questo cliente
      const rentals = rentalsRes.data.filter(rental => 
        rental.customerId === customerId || 
        rental.customer?.id === customerId
      );
      
      setRentalVehicles(vehiclesRes.data);
      setCustomerRentals(rentals);
    } catch (err) {
      console.error('Error fetching customer rentals:', err);
      setCustomerRentals([]);
    } finally {
      setLoadingRentals(false);
    }
  };

  // Function to open rentals dialog
  const handleOpenRentalsDialog = (customer) => {
    setCurrentCustomer(customer);
    fetchCustomerRentals(customer.id);
    setOpenRentalsDialog(true);
  };

  // Function to close rentals dialog
  const handleCloseRentalsDialog = () => {
    setOpenRentalsDialog(false);
    setCustomerRentals([]);
  };

  // Function to handle clicking on a rental
  const handleRentalClick = async (rentalId, rentalGroupId) => {
    try {
      // Se c'è un rentalGroupId, recupera tutti i noleggi del gruppo
      if (rentalGroupId) {
        const rentalsRes = await api.get(`/rentals`);
        const groupRentals = rentalsRes.data.filter(r => r.rentalGroupId === rentalGroupId);
        setSelectedRentalForDetails({ rentals: groupRentals, rentalGroupId });
      } else {
        // Singolo noleggio
        const rentalsRes = await api.get(`/rentals`);
        const rental = rentalsRes.data.find(r => r.id === rentalId);
        setSelectedRentalForDetails({ rentals: [rental], rentalGroupId: null });
      }
      setOpenRentalDetailsDialog(true);
    } catch (err) {
      console.error('Error fetching rental details:', err);
    }
  };

  // Function to close rental details dialog
  const handleCloseRentalDetailsDialog = () => {
    setOpenRentalDetailsDialog(false);
    setSelectedRentalForDetails(null);
  };

  // Helper function to get rental status label
  const getRentalStatusLabel = (status) => {
    const statusLabels = {
      'pending': 'In Attesa',
      'active': 'Attivo',
      'completed': 'Completato',
      'cancelled': 'Annullato'
    };
    return statusLabels[status] || status;
  };

  // Helper function to get rental status color
  const getRentalStatusColor = (status) => {
    const statusColors = {
      'pending': 'warning',
      'active': 'primary',
      'completed': 'success',
      'cancelled': 'error'
    };
    return statusColors[status] || 'default';
  };

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error(`Data non valida: ${dateString}`);
        return 'Data non valida';
      }
      return format(date, 'dd/MM/yyyy', { locale: it });
    } catch (error) {
      console.error(`Errore formattando la data: ${dateString}`, error);
      return 'Data non valida';
    }
  };

  // Function to get status color
  const getSaleStatusColor = (status) => {
    const statusColors = {
      'pending': 'warning',
      'completed': 'success',
      'cancelled': 'error'
    };
    return statusColors[status] || 'default';
  };

  const getSaleStatusLabel = (status) => {
    const statusLabels = {
      'pending': 'In Attesa',
      'completed': 'Completata',
      'cancelled': 'Annullata'
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'In Coda': 'default',
      'In Corso': 'primary',
      'Attesa Ricambi': 'warning',
      'Attesa Cliente': 'info',
      'Da Chiamare': 'secondary',
      'Da Ritirare': 'success',
      'Sollecitato': 'error',
      'Parcheggio': 'default',
      'Consegnato': 'success'
    };
    return statusColors[status] || 'default';
  };

  const handleAddNewType = () => {
    if (newType.trim()) {
      // Aggiungi la nuova tipologia alla lista
      const newTypeValue = newType.trim();
      setVehicleTypes([...vehicleTypes, newTypeValue]);
      
      // Aggiorna anche il veicolo selezionato con la nuova tipologia
      const updatedVehicles = [...formData.vehicles];
      updatedVehicles[selectedVehicleIndex] = {
        ...updatedVehicles[selectedVehicleIndex],
        type: newTypeValue
      };
      
      setFormData({
        ...formData,
        vehicles: updatedVehicles
      });
      
      // Chiudi il dialog e resetta il campo
      setOpenNewTypeDialog(false);
      setNewType('');
    }
  };

  const handleNewTypeChange = (e) => {
    setNewType(e.target.value);
  };

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    const aValue = (a[orderBy] || '').toLowerCase();
    const bValue = (b[orderBy] || '').toLowerCase();
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedCustomers = sortedCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Typography variant="h4">
          Gestione Clienti
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuovo Cliente
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Cerca cliente"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flexGrow: 1 }}
            InputProps={{
              endAdornment: (
                searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => { setSearchTerm(''); fetchCustomers(); }}
                      aria-label="Cancella ricerca"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              )
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Cerca
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ my: 2 }}>{error}</Typography>
        ) : customers.length === 0 ? (
          <Typography sx={{ my: 2 }}>Nessun cliente trovato.</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell onClick={() => handleSort('lastName')} style={{ cursor: 'pointer' }}>
                    Cognome {orderBy === 'lastName' ? (order === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell onClick={() => handleSort('firstName')} style={{ cursor: 'pointer' }}>
                    Nome {orderBy === 'firstName' ? (order === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell>Telefono</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Città</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>{customer.lastName}</TableCell>
                    <TableCell>{customer.firstName}</TableCell>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.city}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSalesDialog(customer)}
                        title="Storico acquisti"
                        color="success"
                      >
                        <AttachMoneyIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenRentalsDialog(customer)}
                        title="Storico noleggi"
                        color="info"
                      >
                        <DirectionsBikeIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenHistoryDialog(customer)}
                        title="Storico manutenzioni"
                      >
                        <HistoryIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(customer)}
                        title="Modifica"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteCustomer(customer.id)}
                        title="Elimina"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={sortedCustomers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[50, 100, 200]}
            />
          </TableContainer>
        )}
      </Paper>

      {/* Customer dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentCustomer ? 'Modifica Cliente' : 'Nuovo Cliente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="firstName"
                label="Nome *"
                fullWidth
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="lastName"
                label="Cognome *"
                fullWidth
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={1}>
                <Grid item xs={3}>
                  <TextField
                    name="countryCode"
                    label="Prefisso"
                    fullWidth
                    value={formData.countryCode}
                    onChange={handleInputChange}
                    required
                    placeholder="39"
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    name="phone"
                    label="Telefono *"
                    fullWidth
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Email"
                fullWidth
                value={formData.email}
                onChange={handleInputChange}
                type="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="address"
                label="Indirizzo"
                fullWidth
                value={formData.address}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="city"
                label="Città"
                fullWidth
                value={formData.city}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="postalCode"
                label="CAP"
                fullWidth
                value={formData.postalCode}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Note"
                fullWidth
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Documento di Riconoscimento */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Documento di Riconoscimento (Richiesto per Noleggi)
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="documentType"
                label="Tipo Documento"
                fullWidth
                select
                value={formData.documentType}
                onChange={handleInputChange}
              >
                <MenuItem value="">Nessuno</MenuItem>
                <MenuItem value="Carta di Identità">Carta di Identità</MenuItem>
                <MenuItem value="Patente">Patente</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="documentNumber"
                label="Numero Documento"
                fullWidth
                value={formData.documentNumber}
                onChange={handleInputChange}
                placeholder="es. CA1234567"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="documentIssueDate"
                label="Data Rilascio"
                type="date"
                fullWidth
                value={formData.documentIssueDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="documentExpiryDate"
                label="Data Scadenza"
                type="date"
                fullWidth
                value={formData.documentExpiryDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                name="documentIssuedBy"
                label="Rilasciato da"
                fullWidth
                value={formData.documentIssuedBy}
                onChange={handleInputChange}
                placeholder="es. Comune di Roma"
              />
            </Grid>

            {/* Vehicles section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Veicoli
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {formData.vehicles.map((vehicle, index) => (
                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1">
                        Veicolo {index + 1}
                        {formData.vehicles.length > 1 && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removeVehicle(index)}
                            sx={{ ml: 2 }}
                          >
                            Rimuovi
                          </Button>
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name={`vehicle-${index}-brand`}
                        label="Marca *"
                        fullWidth
                        value={vehicle.brand}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name={`vehicle-${index}-model`}
                        label="Modello *"
                        fullWidth
                        value={vehicle.model}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name={`vehicle-${index}-type`}
                        label="Tipologia *"
                        fullWidth
                        select
                        value={vehicle.type}
                        onChange={handleInputChange}
                        required
                      >
                        {vehicleTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                        <MenuItem value="new_type" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                          + Aggiungi nuova tipologia
                        </MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name={`vehicle-${index}-serialNumber`}
                        label="Numero Telaio"
                        fullWidth
                        value={vehicle.serialNumber}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name={`vehicle-${index}-color`}
                        label="Colore"
                        fullWidth
                        value={vehicle.color}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name={`vehicle-${index}-notes`}
                        label="Note Veicolo"
                        fullWidth
                        multiline
                        rows={2}
                        value={vehicle.notes}
                        onChange={handleInputChange}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addVehicle}
                sx={{ mt: 1 }}
              >
                Aggiungi Veicolo
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={savingCustomer}>
            {savingCustomer ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance History Dialog */}
      <Dialog open={openHistoryDialog} onClose={handleCloseHistoryDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Storico Manutenzioni - {currentCustomer ? `${currentCustomer.firstName} ${currentCustomer.lastName}` : ''}
        </DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : maintenanceHistory.length > 0 ? (
            <List>
              {maintenanceHistory.map((maintenance) => (
                <ListItem 
                  key={maintenance.id} 
                  button 
                  onClick={() => handleMaintenanceClick(maintenance.id)}
                  divider
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1">
                          Protocollo #{maintenance.protocolNumber}
                        </Typography>
                        <Chip 
                          label={maintenance.status} 
                          color={getStatusColor(maintenance.status)} 
                          size="small" 
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          Data: {formatDate(maintenance.createdAt)}
                        </Typography>
                        <br />
                        <Typography variant="body2" component="span">
                          {maintenance.description.substring(0, 100)}
                          {maintenance.description.length > 100 ? '...' : ''}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              Nessuna manutenzione trovata per questo cliente
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Customer Sales Dialog */}
      <Dialog open={openSalesDialog} onClose={handleCloseSalesDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Storico Acquisti - {currentCustomer ? `${currentCustomer.firstName} ${currentCustomer.lastName}` : ''}
        </DialogTitle>
        <DialogContent>
          {loadingSales ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : customerSales.length > 0 ? (
            <List>
              {customerSales.map((sale) => (
                <ListItem
                  key={sale.id}
                  button
                  onClick={() => handleSaleClick(sale.id)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1">
                          Vendita #{sale.id}
                        </Typography>
                        <Chip 
                          label={getSaleStatusLabel(sale.status)} 
                          color={getSaleStatusColor(sale.status)} 
                          size="small" 
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span">
                          Data: {formatDate(sale.createdAt)}
                        </Typography>
                        <br />
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          Totale: €{sale.totalAmount ? parseFloat(sale.totalAmount).toFixed(2) : sale.paymentAmount ? parseFloat(sale.paymentAmount).toFixed(2) : '0.00'}
                        </Typography>
                        {sale.items && sale.items.length > 0 && (
                          <>
                            <br />
                            <Typography variant="caption" component="span" color="text.secondary">
                              Articoli: {sale.items.length}
                            </Typography>
                          </>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              Nessun acquisto trovato per questo cliente
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSalesDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Sale Details Dialog */}
      <Dialog
        open={openSaleDetailsDialog}
        onClose={handleCloseSaleDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" component="span">Dettagli Vendita</Typography>
            {selectedSaleForDetails && (
              <Typography variant="subtitle2" color="text.secondary" component="span">
                {formatDate(selectedSaleForDetails.createdAt)}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSaleForDetails && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Cliente: {selectedSaleForDetails.Customer ? `${selectedSaleForDetails.Customer.firstName} ${selectedSaleForDetails.Customer.lastName}` : selectedSaleForDetails.customer ? `${selectedSaleForDetails.customer.firstName} ${selectedSaleForDetails.customer.lastName}` : 'Cliente sconosciuto'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip
                    label={getSaleStatusLabel(selectedSaleForDetails.status)}
                    color={getSaleStatusColor(selectedSaleForDetails.status)}
                    size="small"
                  />
                  
                  {selectedSaleForDetails.status === 'completed' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {selectedSaleForDetails.paymentDate && (
                        <Typography variant="caption" color="text.secondary">
                          Pagato il: {formatDate(selectedSaleForDetails.paymentDate)}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Importo: €{selectedSaleForDetails.paymentAmount ? parseFloat(selectedSaleForDetails.paymentAmount).toFixed(2) : selectedSaleForDetails.totalAmount ? parseFloat(selectedSaleForDetails.totalAmount).toFixed(2) : '0.00'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              
              {selectedSaleForDetails.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Note: {selectedSaleForDetails.notes}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Articoli
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Prodotto</TableCell>
                        <TableCell align="center">Quantità</TableCell>
                        <TableCell align="right">Prezzo Vendita</TableCell>
                        <TableCell align="right">Totale</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSaleForDetails.items && selectedSaleForDetails.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2">
                              {item.description || item.Product?.name || 'Senza descrizione'}
                            </Typography>
                            {item.includeLabor && (
                              <Typography variant="caption" color="text.secondary">
                                Include manodopera
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">€{parseFloat(item.price || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">€{(parseFloat(item.price || 0) * item.quantity).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      {(!selectedSaleForDetails.items || selectedSaleForDetails.items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              Nessun articolo presente
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Typography variant="h6">
                    Totale: €{selectedSaleForDetails.totalAmount ? parseFloat(selectedSaleForDetails.totalAmount).toFixed(2) : selectedSaleForDetails.paymentAmount ? parseFloat(selectedSaleForDetails.paymentAmount).toFixed(2) : '0.00'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaleDetailsDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Customer Rentals Dialog */}
      <Dialog open={openRentalsDialog} onClose={handleCloseRentalsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Storico Noleggi - {currentCustomer ? `${currentCustomer.firstName} ${currentCustomer.lastName}` : ''}
        </DialogTitle>
        <DialogContent>
          {loadingRentals ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : customerRentals.length > 0 ? (
            <List>
              {(() => {
                // Raggruppa i noleggi per rentalGroupId
                const groupedRentals = {};
                const singleRentals = [];
                
                customerRentals.forEach(rental => {
                  if (rental.rentalGroupId) {
                    if (!groupedRentals[rental.rentalGroupId]) {
                      groupedRentals[rental.rentalGroupId] = [];
                    }
                    groupedRentals[rental.rentalGroupId].push(rental);
                  } else {
                    singleRentals.push(rental);
                  }
                });
                
                const displayItems = [];
                
                // Aggiungi i gruppi
                Object.values(groupedRentals).forEach(group => {
                  const firstRental = group[0];
                  const totalAmount = group.reduce((sum, r) => sum + parseFloat(r.totalAmount || 0), 0);
                  const totalDiscount = group.reduce((sum, r) => sum + parseFloat(r.sconto || 0), 0);
                  const totalAfterDiscount = totalAmount - totalDiscount;
                  
                  displayItems.push({
                    id: firstRental.id,
                    rentalGroupId: firstRental.rentalGroupId,
                    startDate: firstRental.startDate,
                    endDate: firstRental.endDate,
                    status: firstRental.status,
                    totalAmount: totalAfterDiscount,
                    vehicleCount: group.length,
                    rentals: group
                  });
                });
                
                // Aggiungi i noleggi singoli
                singleRentals.forEach(rental => {
                  const totalDiscount = parseFloat(rental.sconto || 0);
                  const totalAfterDiscount = parseFloat(rental.totalAmount || 0) - totalDiscount;
                  
                  displayItems.push({
                    id: rental.id,
                    rentalGroupId: null,
                    startDate: rental.startDate,
                    endDate: rental.endDate,
                    status: rental.status,
                    totalAmount: totalAfterDiscount,
                    vehicleCount: 1,
                    rentals: [rental]
                  });
                });
                
                // Ordina per data di creazione (più recenti prima)
                displayItems.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
                
                return displayItems.map((item) => {
                  const days = Math.max(1, Math.floor((new Date(item.endDate) - new Date(item.startDate)) / (1000 * 60 * 60 * 24)));
                  return (
                    <ListItem
                      key={item.id}
                      button
                      onClick={() => handleRentalClick(item.id, item.rentalGroupId)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1">
                              Noleggio #{item.id}
                              {item.vehicleCount > 1 && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                  ({item.vehicleCount} veicoli)
                                </Typography>
                              )}
                            </Typography>
                            <Chip 
                              label={getRentalStatusLabel(item.status)} 
                              color={getRentalStatusColor(item.status)} 
                              size="small" 
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              Periodo: {formatDate(item.startDate)} - {formatDate(item.endDate)} ({days} {days === 1 ? 'giorno' : 'giorni'})
                            </Typography>
                            <br />
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                              Totale: €{item.totalAmount.toFixed(2)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  );
                });
              })()}
            </List>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center' }}>
              Nessun noleggio trovato per questo cliente
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRentalsDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Rental Details Dialog */}
      <Dialog
        open={openRentalDetailsDialog}
        onClose={handleCloseRentalDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" component="span">Dettagli Noleggio</Typography>
            {selectedRentalForDetails && selectedRentalForDetails.rentals && selectedRentalForDetails.rentals[0] && (
              <Typography variant="subtitle2" color="text.secondary" component="span">
                {formatDate(selectedRentalForDetails.rentals[0].startDate)} - {formatDate(selectedRentalForDetails.rentals[0].endDate)}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRentalForDetails && selectedRentalForDetails.rentals && selectedRentalForDetails.rentals.length > 0 && (() => {
            const rentals = selectedRentalForDetails.rentals;
            const firstRental = rentals[0];
            const totalAmount = rentals.reduce((sum, r) => sum + parseFloat(r.totalAmount || 0), 0);
            const totalDiscount = rentals.reduce((sum, r) => sum + parseFloat(r.sconto || 0), 0);
            const totalAfterDiscount = totalAmount - totalDiscount;
            const totalDeposit = rentals.reduce((sum, r) => sum + parseFloat(r.cauzione || 0), 0);
            const totalDownPayment = rentals.reduce((sum, r) => sum + parseFloat(r.acconto || 0), 0);
            const totalPaid = rentals.reduce((sum, r) => sum + parseFloat(r.totalePagato || 0), 0);
            const remaining = totalAfterDiscount - totalPaid;
            const allPaid = rentals.every(r => r.saldato === true);
            const days = Math.max(1, Math.floor((new Date(firstRental.endDate) - new Date(firstRental.startDate)) / (1000 * 60 * 60 * 24)));
            
            return (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Cliente</Typography>
                    <Typography variant="h6">{currentCustomer ? `${currentCustomer.firstName} ${currentCustomer.lastName}` : 'N/A'}</Typography>
                    {currentCustomer && (
                      <Box sx={{ mt: 1 }}>
                        {currentCustomer.email && <Typography variant="body2" color="text.secondary">📧 {currentCustomer.email}</Typography>}
                        {currentCustomer.phone && <Typography variant="body2" color="text.secondary">📞 {currentCustomer.phone}</Typography>}
                      </Box>
                    )}
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Periodo</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {formatDate(firstRental.startDate)} - {formatDate(firstRental.endDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {days} {days === 1 ? 'giorno' : 'giorni'}
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Stato</Typography>
                    <Chip label={getRentalStatusLabel(firstRental.status)} color={getRentalStatusColor(firstRental.status)} sx={{ mt: 0.5 }} />
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                      Dati Economici
                    </Typography>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Totale Noleggio:</Typography>
                        <Typography variant="body2" fontWeight={600}>€{totalAmount.toFixed(2)}</Typography>
                      </Box>
                      {totalDiscount > 0 && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Sconto:</Typography>
                            <Typography variant="body2" fontWeight={600}>-€{totalDiscount.toFixed(2)}</Typography>
                          </Box>
                          <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" fontWeight={600}>Totale con Sconto:</Typography>
                            <Typography variant="body2" fontWeight={700}>€{totalAfterDiscount.toFixed(2)}</Typography>
                          </Box>
                        </>
                      )}
                      {totalDownPayment > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 1 }}>
                          <Typography variant="body2">Acconto Versato:</Typography>
                          <Typography variant="body2" fontWeight={600}>€{totalDownPayment.toFixed(2)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 1 }}>
                        <Typography variant="body2">Totale Pagato:</Typography>
                        <Typography variant="body2" fontWeight={600}>€{totalPaid.toFixed(2)}</Typography>
                      </Box>
                      {remaining > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 1 }}>
                          <Typography variant="body2">Residuo da Pagare:</Typography>
                          <Typography variant="body2" fontWeight={700} color="warning.main">€{remaining.toFixed(2)}</Typography>
                        </Box>
                      )}
                      {totalDeposit > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, mt: 1 }}>
                          <Typography variant="body2">Cauzione:</Typography>
                          <Typography variant="body2" fontWeight={600}>€{totalDeposit.toFixed(2)}</Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.3)' }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" fontWeight={600}>
                          Stato Pagamento:
                        </Typography>
                        <Chip 
                          label={allPaid ? 'Saldato' : 'In Attesa'} 
                          color={allPaid ? 'success' : 'warning'}
                          size="small"
                          sx={{ bgcolor: allPaid ? 'success.main' : 'warning.main', color: 'white' }}
                        />
                      </Box>
                    </Box>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Veicoli Noleggiati ({rentals.length})
                    </Typography>
                    <List dense>
                      {rentals.map((rental, idx) => {
                        const vehicle = rentalVehicles.find(v => v.id === rental.vehicleId);
                        return (
                          <ListItem key={idx} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DirectionsBikeIcon color="primary" fontSize="small" />
                                  <Typography variant="body1" fontWeight={500}>
                                    {vehicle ? vehicle.name : 'N/A'}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <>
                                  {vehicle && (
                                    <Typography variant="caption" color="text.secondary">
                                      {vehicle.type} • {vehicle.model}
                                      {vehicle.serialNumber && ` • SN: ${vehicle.serialNumber}`}
                                      {vehicle.color && ` • ${vehicle.color}`}
                                    </Typography>
                                  )}
                                  <br />
                                  <Typography variant="caption" fontWeight={600}>
                                    €{parseFloat(rental.totalAmount || 0).toFixed(2)}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Card>
                </Grid>
                
                {firstRental.notes && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Note</Typography>
                      <Typography variant="body2">{firstRental.notes}</Typography>
                    </Card>
                  </Grid>
                )}
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRentalDetailsDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance Details Dialog */}
      {selectedMaintenanceId && (
        <MaintenanceDetails
          open={openMaintenanceDetails}
          onClose={handleCloseMaintenanceDetails}
          maintenanceId={selectedMaintenanceId}
        />
      )}

      {/* Dialog for adding new vehicle type */}
      <Dialog open={openNewTypeDialog} onClose={() => setOpenNewTypeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Aggiungi Nuova Tipologia</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Inserisci il nome della nuova tipologia di veicolo:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            name="newType"
            label="Nome Tipologia"
            type="text"
            fullWidth
            variant="outlined"
            value={newType}
            onChange={handleNewTypeChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewTypeDialog(false)} color="primary">
            Annulla
          </Button>
          <Button onClick={handleAddNewType} color="primary" variant="contained">
            Aggiungi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerList;